import React, { memo, useState } from 'react'
import {
  CButton,
  CCol,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
} from '@coreui/react-pro'
import { withdrawalRejectionReason } from '../../../const'
import { useOnClickOutside } from 'usehooks-ts'
import { $reject, setReject, submitReject } from './model'
import { useStore } from 'effector-react'
import reactStringReplace from 'react-string-replace'
import { Divider } from 'antd'

type Props = {
  id: string
  isVisible: boolean
  onClose: () => void
}

type RadioProps = {
  ref?: React.Ref<HTMLDivElement> | undefined
  value: string
  label: string
  reasonName: string
  className?: string
  color?: string
  disabled?: boolean
}

export const Radio = memo(
  ({ value, label, reasonName, className, color, disabled }: RadioProps) => {
    const word = reasonName
    const firstLetter = word.charAt(0)
    const firstLetterCap = firstLetter.toUpperCase()
    const remainingLetters = word.slice(1)
    const CapitalizedReasonName = firstLetterCap + remainingLetters

    function placeholderReplacer(text: string) {
      return (
        <>
          {reactStringReplace(text, /\{(.*?)}/g, (match: string, i: any) => (
            <CFormInput
              type="text"
              id={'rejectReason' + match + 'Placeholder'}
              name={'rejectReason' + match + 'Placeholder'}
              size="sm"
              className="w-25"
            />
          ))}
        </>
      )
    }

    return (
      <>
        <CRow className="g-3">
          <CCol className="d-flex flex-row gap-2">
            <CFormCheck
              type="radio"
              name="rejectReason"
              id={'rejectReason' + CapitalizedReasonName}
              value={value}
              onChange={(e) => {
                setReject({
                  inputs: 'rejectReason' + CapitalizedReasonName + 'label',
                  rejectReason: e.target.value,
                })
              }}
            />
            <CFormLabel
              htmlFor={'rejectReason' + CapitalizedReasonName}
              className="d-flex flex-row w-75 gap-2"
              id={'rejectReason' + CapitalizedReasonName + 'label'}
            >
              {placeholderReplacer(label)}
            </CFormLabel>
          </CCol>
        </CRow>
        <Divider className="m-2 mb-3" />
      </>
    )
  },
)

export const RejectModal: React.FC<Props> = ({ isVisible, onClose, id }) => {
  const reject = useStore($reject)

  const [reason, setReason] = useState('')

  const ref = React.useRef(null)

  useOnClickOutside(ref, () => {
    if (isVisible) {
      setReject({ rejectReason: '', inputs: '' })
    }
  })

  return (
    <CModal visible={isVisible} onClose={onClose} size={'xl'}>
      <CForm
        className="row g-3 needs-validation"
        noValidate
        onSubmit={(e) => {
          if (reject.rejectReason.length == 0) {
            setReason('Please select a reason')
            e.preventDefault()
            e.stopPropagation()
            return
          }
          e.preventDefault()
          setReason('')
          let children = document
            .getElementById(reject.inputs)
            ?.getElementsByTagName('input')
          // @ts-ignore
          let inputs = {}
          // @ts-ignore
          inputs['params'] = {}
          if (children) {
            for (let i = 0; i < children.length; i++) {
              let name = children[i].id
              name = name.replace('rejectReason', '')
              name = name.replace('Placeholder', '')
              // @ts-ignore
              inputs['params']['{' + name + '}'] = children[i].value
            }
          }

          // @ts-ignore
          inputs['key'] = reject.rejectReason

          submitReject({
            requestId: id,
            rejectReason: JSON.stringify(inputs),
          })
          onClose()
        }}
        id="rejectForm"
      >
        <CModalHeader>
          <CModalTitle>Reject modal</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <Radio
            value={withdrawalRejectionReason.notMoney().key}
            label={withdrawalRejectionReason.notMoney().label}
            reasonName={'notMoney'}
          />
          <Radio
            value={withdrawalRejectionReason.wrongEwalletNumber().key}
            label={withdrawalRejectionReason.wrongEwalletNumber().label}
            reasonName={'wrongEwalletNumber'}
          />
          <Radio
            value={withdrawalRejectionReason.wrongBankNumber().key}
            label={withdrawalRejectionReason.wrongBankNumber().label}
            reasonName={'wrongBankNumber'}
          />
          <Radio
            value={withdrawalRejectionReason.ewalletWrongName().key}
            label={withdrawalRejectionReason.ewalletWrongName().label}
            reasonName={'ewalletWrongName'}
          />
          <Radio
            value={withdrawalRejectionReason.bankStrangeName().key}
            label={withdrawalRejectionReason.bankStrangeName().label}
            reasonName={'bankStrangeName'}
          />
          <Radio
            value={withdrawalRejectionReason.toPs().key}
            label={withdrawalRejectionReason.toPs().label}
            reasonName={'toPs'}
          />
          <Radio
            value={withdrawalRejectionReason.support().key}
            label={withdrawalRejectionReason.support().label}
            reasonName={'support'}
          />
          <Radio
            value={withdrawalRejectionReason.makeAmountToEwallet().key}
            label={withdrawalRejectionReason.makeAmountToEwallet().label}
            reasonName={'makeAmountToEwallet'}
          />
          <Radio
            value={withdrawalRejectionReason.bankWrongName().key}
            label={withdrawalRejectionReason.bankWrongName().label}
            reasonName={'bankWrongName'}
          />
          <Radio
            value={withdrawalRejectionReason.onlyOwnAccount().key}
            label={withdrawalRejectionReason.onlyOwnAccount().label}
            reasonName={'onlyOwnAccount'}
          />
          <Radio
            value={withdrawalRejectionReason.confirmEwallet().key}
            label={withdrawalRejectionReason.confirmEwallet().label}
            reasonName={'confirmEwallet'}
          />
          <Radio
            value={withdrawalRejectionReason.maintenance().key}
            label={withdrawalRejectionReason.maintenance().label}
            reasonName={'maintenance'}
          />
          <Radio
            value={withdrawalRejectionReason.partnership().key}
            label={withdrawalRejectionReason.partnership().label}
            reasonName={'partnership'}
          />
          <Radio
            value={withdrawalRejectionReason.idToSupport().key}
            label={withdrawalRejectionReason.idToSupport().label}
            reasonName={'idToSupport'}
          />
          <Radio
            value={withdrawalRejectionReason.cardCanAccept().key}
            label={withdrawalRejectionReason.cardCanAccept().label}
            reasonName={'cardCanAccept'}
          />
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={onClose}>
            Cancel
          </CButton>
          <CButton
            color="primary"
            className="px-4"
            type="submit"
            // disabled={isLoading}
          >
            {/*{isLoading && (*/}
            {/*  <span className="spinner-border spinner-border-sm"></span>*/}
            {/*)}*/}
            Ok
          </CButton>
        </CModalFooter>
      </CForm>
    </CModal>
  )
}
